import type { ReactNode } from 'react';

import { Card } from '../../../components/ui/card';
import { cn } from '../../../lib/utils';
import { PhaseSubTabs, type PhaseSubTabsProps } from './PhaseSubTabs';
import { DataTablePagination } from '../../../components/common/DataTable/DataTablePagination';
import { StickyTableToolbar } from '../../../components/common/DataTable/StickyTableToolbar';

export interface RosterTableShellProps<K extends string> {
  /** Tab definitions forwarded to PhaseSubTabs. */
  tabs: PhaseSubTabsProps<K>['tabs'];
  activeTab: K;
  onTabChange: (tab: K) => void;
  /** Show skeleton pill-row while counts are loading. */
  tabsLoading?: boolean;
  /** Pagination state. */
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
  pageSize?: number;
  /** Rows-per-page choices + handler. When both are supplied the paginator
   *  renders a size selector; omit them to hide it. */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (n: number) => void;
  /** Entity noun for the footer count ("Showing X–Y of Z creators"). */
  noun?: string;
  /** Suppresses the "Showing X–Y of Z" summary text (skeletons it instead)
   *  while keeping the real page-number buttons — see DataTableProps'
   *  `pagination.hideSummary` doc comment. Use when `total` isn't a real,
   *  known aggregate. */
  hideSummary?: boolean;
  /** Optional right-aligned controls on the sub-tab row (Figma: search / sort
   *  / filters live beside the pills). When omitted the pill row renders alone
   *  (backward-compatible for tabs that don't need controls). */
  controls?: ReactNode;
  /** Dual-scroll mode: the pills + controls row pins under the top bar (via
   *  StickyTableToolbar) while the KPIs above scroll away, and the table card
   *  caps to the remaining viewport and scrolls its rows internally (paginator
   *  pinned at the card bottom). The child DataTable must also get `fillHeight`.
   *  Requires the page to scroll (campaign-detail must NOT bound/overflow-hidden
   *  the content for these tabs). */
  fill?: boolean;
  /** Table content — loading/empty/data states live here. */
  children: ReactNode;
}

/**
 * Shared shell used by every roster tab.
 *
 * Renders: Card wrapper → PhaseSubTabs header → children slot → paginator.
 * All tabs use the same visual container so sub-tab style, card rounding,
 * and pagination placement are consistent everywhere.
 */
export function RosterTableShell<K extends string>({
  tabs,
  activeTab,
  onTabChange,
  tabsLoading,
  page,
  totalPages,
  total,
  onPage,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  noun,
  hideSummary,
  controls,
  fill,
  children,
}: RosterTableShellProps<K>) {
  // Figma: the sub-tab pills (+ optional search/sort/filter controls) live ABOVE
  // the table on the page background — they are NOT part of the table card.
  // `bare` strips PhaseSubTabs' card-header wrapper so the pills stand alone.
  const subTabs = (
    <PhaseSubTabs<K> active={activeTab} tabs={tabs} onChange={onTabChange} loading={tabsLoading} bare />
  );
  const pager = (
    <DataTablePagination
      pagination={{
        page,
        pageSize: pageSize ?? 30,
        total,
        totalPages,
        onPageChange: onPage,
        noun,
        pageSizeOptions,
        onPageSizeChange,
        hideSummary,
      }}
    />
  );
  const headerRow = (
    <>
      {subTabs}
      {controls ? <div className="flex items-center gap-5">{controls}</div> : null}
    </>
  );
  return (
    <div className="flex flex-1 flex-col gap-5">
      {/* Sub-tab pills + controls row. In `fill` (dual-scroll) mode it pins under
          the 52px top bar and publishes its height as `--dt-top-offset` so the
          card below caps to the remaining viewport. */}
      {fill ? (
        <StickyTableToolbar className="flex flex-wrap items-center justify-between gap-4">
          {headerRow}
        </StickyTableToolbar>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-4">{headerRow}</div>
      )}

      {/* Figma 1177:34690: the card hugs its content — the paginator sits
          directly under the last row, with no height-fill gap between them.
          In `fill` mode the card additionally caps at the viewport height left
          below the pinned header and the table scrolls inside it (paginator
          stays pinned at the bottom). */}
      <Card
        // cn() keeps `p-0` and the fill-only classes as separate string args so a
        // future `eslint --fix` tailwind pass can't glue them into an invalid
        // `p-0min-h-0` (that regression dropped `p-0`, letting Card's default
        // `py-6` show as white space above the header and below the paginator).
        className={cn(
          'flex flex-col gap-0 overflow-hidden rounded-xl p-0',
          fill && 'min-h-0 max-h-[calc(100dvh-var(--dt-top-offset,13rem))]',
        )}
      >
        {children}

        {fill ? <div className="shrink-0">{pager}</div> : pager}
      </Card>
    </div>
  );
}
